/**
 * Auth store — utilisateur courant + access token (mémoire seule).
 *
 * @remarks
 * **Sécurité** : `accessToken` vit en **mémoire seulement** (jamais persisté).
 * Au reload, `lib/auth/refresh.ts` fait un silent refresh via le cookie
 * HTTP-only `refresh_token` pour régénérer un access token frais.
 *
 * **Persisté** : seules les infos non-sensibles de l'utilisateur (id, email,
 * roles, permissions, settings…) survivent au reload — pour montrer une UI
 * cohérente pendant que le refresh se fait.
 *
 * **`setSession`** consomme une `SessionData` (forme commune entre
 * `SignInResponse` et `RefreshTokenResponse`) → utilisable depuis les deux
 * mutations TanStack Query.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { AuthenticatedUser } from '@/app/registry';
import type { SessionData } from '@/lib/http/types';
import { queryClient } from '@/lib/query/query-client';

interface AuthState {
  /** JWT access token — en mémoire seule, jamais persisté. */
  accessToken: string | null;

  /** Utilisateur courant — persisté (sans le token). */
  user: AuthenticatedUser | null;

  /** Dérivé — vrai si `accessToken` ET `user` sont définis. */
  isAuthenticated: boolean;

  /** Stocke une session fraîche (sign-in ou refresh). */
  setSession: (data: SessionData) => void;

  /** Met à jour uniquement le bloc `user.settings`. */
  updateUserSettings: (settings: Record<string, unknown>) => void;

  /** Met à jour les champs profil (firstname, lastname…) après un PATCH /api/users/me. */
  patchUser: (partial: Partial<AuthenticatedUser>) => void;

  /**
   * La session a expiré en cours d'usage (SAAS-1266) : le refresh a échoué
   * alors que l'utilisateur était connecté. Le shell authentifié le voit et
   * renvoie vers `/login`. Jamais persisté.
   */
  sessionExpired: boolean;

  /**
   * Clear tout. Appelé explicitement (logout button) ou auto par l'interceptor 401.
   * `expired` : la déconnexion n'est pas voulue, la session a expiré.
   */
  logout: (options?: { expired?: boolean }) => void;

  /** Le shell a pris en compte l'expiration (redirection faite). */
  acknowledgeSessionExpired: () => void;

  /** Helper : l'utilisateur a-t-il cette permission ? */
  hasPermission: (permission: string) => boolean;

  /** Helper : l'utilisateur a-t-il ce rôle ? */
  hasRole: (role: string) => boolean;

  /** @internal Test helper. */
  _reset: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        accessToken: null,
        user: null,
        isAuthenticated: false,
        sessionExpired: false,

        setSession: (data) => {
          const previousUserId = get().user?.id;
          // Switch de compte sans logout explicite (ex: silent refresh qui
          // résout sur un autre user) → on purge le cache TanStack Query du
          // user précédent pour ne pas lui servir ses données serveur (SAAS-547).
          // No-op sur un silent refresh du MÊME user (cas nominal au reload).
          if (previousUserId !== undefined && previousUserId !== data.user.id) {
            queryClient.clear();
          }
          set({
            accessToken: data.accessToken,
            user: data.user,
            isAuthenticated: true,
            sessionExpired: false,
          });
        },

        updateUserSettings: (settings) => {
          const u = get().user;
          if (!u) return;
          set({ user: { ...u, settings } });
        },

        patchUser: (partial) => {
          const u = get().user;
          if (!u) return;
          set({ user: { ...u, ...partial } });
        },

        logout: (options) => {
          // Un visiteur jamais connecté dont le refresh échoue au chargement
          // n'a pas « perdu » de session : pas de message d'expiration.
          const sessionExpired = options?.expired === true && get().isAuthenticated;
          set({ accessToken: null, user: null, isAuthenticated: false, sessionExpired });
          // Purge tout le cache serveur du user déconnecté — sinon ses données
          // (profil, etc.) restent en mémoire et fuitent vers le prochain user
          // connecté. Couvre les 3 chemins de logout : bouton (performLogout),
          // silent refresh échoué (lib/auth/refresh) et interceptor 401
          // (lib/http/client). Cf. SAAS-547.
          queryClient.clear();
        },

        acknowledgeSessionExpired: () => {
          set({ sessionExpired: false });
        },

        hasPermission: (permission) => {
          const u = get().user;
          return u?.permissions.includes(permission) ?? false;
        },

        hasRole: (role) => {
          const u = get().user;
          return u?.roles.includes(role) ?? false;
        },

        _reset: () => {
          set({ accessToken: null, user: null, isAuthenticated: false, sessionExpired: false });
        },
      }),
      {
        name: 'saas-front-v2:auth',
        version: 4,
        // ⚠️ Ne JAMAIS persister `accessToken` ni `isAuthenticated`.
        // - accessToken : sécurité (XSS protection).
        // - isAuthenticated : se redérive du loader requireAuth au reload
        //   (le silent refresh décide).
        partialize: (state) => ({ user: state.user }),
        /**
         * Toute version antérieure force une reconnexion.
         *
         * @remarks
         * **Zustand n'appelle `migrate` que si la version persistée diffère de
         * la version courante.** Le code testait `oldVersion === 4` alors que
         * le store est en `version: 4` : la branche n'était jamais atteinte, et
         * la fonction retournait toujours `{ user: null }`.
         *
         * Le comportement voulu était respecté — par accident. Le piège était
         * pour le prochain bump : passer en `version: 5` en recopiant ce motif
         * aurait donné une branche `oldVersion === 5` tout aussi morte, en
         * croyant préserver l'état. (SAAS-903)
         *
         * Pour préserver un état d'une version antérieure, comparer à la
         * version **précédente** — `if (oldVersion === 4) { …adapter… }` une
         * fois passé en 5 — et n'adapter que ce qui a changé de forme. Sans
         * branche explicite, on retombe sur la reconnexion : mieux vaut un
         * login de plus qu'un `user` partiellement formé qui casse l'app.
         */
        migrate: () => ({ user: null }),
      },
    ),
    { name: 'auth', enabled: import.meta.env.DEV },
  ),
);
